import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';

export interface UrlSerpSnapshotsChunkData {
  domain: string;
  trackedUrls: number;
  resolvedQueries: number;
  uniqueKeywords: number;
  keywordOffset: number;
  keywordsProcessedThisChunk: number;
  nextKeywordOffset: number | null;
  topicSerpCacheHits: number;
  apiFetches: number;
  keywordDifficultyCacheHits: number;
  keywordDifficultyApiFetches: number;
  snapshotsWritten: number;
  estimatedSerpCostUsd: number;
  estimatedKeywordDifficultyCostUsd: number;
  failedKeywords: number;
}

export interface UrlSerpRefreshProgress {
  percent: number;
  keywordsDone: number;
  totalKeywords: number;
  statusLine: string;
  etaLabel: string | null;
  chunksCompleted: number;
  runningCostUsd: number;
}

export interface UrlSerpRefreshTotals {
  totalKeywords: number;
  snapshotsWritten: number;
  apiFetches: number;
  cacheHits: number;
  failedKeywords: number;
  estimatedCostUsd: number;
  elapsedSec: number;
}

@Injectable({ providedIn: 'root' })
export class UrlSerpSyncService {
  private readonly API_URL = environment.apiUrl + '/v1';
  readonly chunkSize = 10;
  readonly chunkDelayMs = 150;

  constructor(private http: HttpClient) {}

  refreshDomainBatched(
    domain: string,
    options: {
      forceRefresh?: boolean;
      onProgress?: (progress: UrlSerpRefreshProgress) => void;
    } = {}
  ): Promise<UrlSerpRefreshTotals> {
    return this.runChunks(domain, options);
  }

  /** One page, one target query — no domain-wide batch loop. */
  refreshPageUrl(
    pageUrl: string,
    options: { forceRefresh?: boolean; onProgress?: (progress: UrlSerpRefreshProgress) => void } = {}
  ): Promise<UrlSerpRefreshTotals> {
    const startedAt = Date.now();
    options.onProgress?.({
      percent: 0,
      keywordsDone: 0,
      totalKeywords: 1,
      statusLine: options.forceRefresh
        ? 'Fetching SERP from API…'
        : 'Fetching SERP (cache-first)…',
      etaLabel: null,
      chunksCompleted: 0,
      runningCostUsd: 0
    });

    return firstValueFrom(
      this.http.post<{
        success: boolean;
        message?: string;
        data?: UrlSerpSnapshotsChunkData & { queryDisplay?: string | null; pageUrl?: string };
      }>(`${this.API_URL}/urls/process/url-serp-snapshot`, {
        url: pageUrl,
        forceRefresh: options.forceRefresh === true
      })
    ).then((res) => {
      if (!res?.success || !res.data) {
        throw new Error(res?.message || 'URL SERP refresh returned no data');
      }

      const d = res.data;
      const elapsedSec = Math.round((Date.now() - startedAt) / 1000);
      const queryLabel = d.queryDisplay ? `"${d.queryDisplay}"` : 'target query';

      options.onProgress?.({
        percent: 100,
        keywordsDone: 1,
        totalKeywords: 1,
        statusLine: `Done · ${queryLabel} · ${d.apiFetches ? 'API' : 'cached'} · ${d.snapshotsWritten} snapshot`,
        etaLabel: `Finished in ${elapsedSec}s`,
        chunksCompleted: 1,
        runningCostUsd: (d.estimatedSerpCostUsd || 0) + (d.estimatedKeywordDifficultyCostUsd || 0)
      });

      return {
        totalKeywords: d.uniqueKeywords || 1,
        snapshotsWritten: d.snapshotsWritten,
        apiFetches: d.apiFetches,
        cacheHits: d.topicSerpCacheHits,
        failedKeywords: d.failedKeywords,
        estimatedCostUsd: (d.estimatedSerpCostUsd || 0) + (d.estimatedKeywordDifficultyCostUsd || 0),
        elapsedSec
      };
    });
  }

  private async runChunks(
    domain: string,
    options: { forceRefresh?: boolean; onProgress?: (progress: UrlSerpRefreshProgress) => void }
  ): Promise<UrlSerpRefreshTotals> {
    const startedAt = Date.now();
    let keywordOffset = 0;
    let totalKeywords = 0;
    let chunksCompleted = 0;
    let sumSnapshots = 0;
    let sumApi = 0;
    let sumCache = 0;
    let sumFailedKw = 0;
    let sumCost = 0;

    options.onProgress?.({
      percent: 0,
      keywordsDone: 0,
      totalKeywords: 0,
      statusLine: 'Starting URL SERP refresh…',
      etaLabel: null,
      chunksCompleted: 0,
      runningCostUsd: 0
    });

    while (true) {
      const res = await firstValueFrom(
        this.http.post<{ success: boolean; message?: string; data?: UrlSerpSnapshotsChunkData }>(
          `${this.API_URL}/domains/${encodeURIComponent(domain)}/process/url-serp-snapshots`,
          {
            forceRefresh: options.forceRefresh === true,
            keywordOffset,
            maxKeywords: this.chunkSize
          }
        )
      );

      if (!res?.success || !res.data) {
        throw new Error(res?.message || 'URL SERP refresh returned no data.');
      }

      const d = res.data;
      chunksCompleted += 1;
      if (totalKeywords === 0) {
        totalKeywords = d.uniqueKeywords;
      }

      sumSnapshots += d.snapshotsWritten;
      sumApi += d.apiFetches;
      sumCache += d.topicSerpCacheHits;
      sumFailedKw += d.failedKeywords;
      sumCost += (d.estimatedSerpCostUsd || 0) + (d.estimatedKeywordDifficultyCostUsd || 0);

      const keywordsDone =
        d.nextKeywordOffset != null ? d.nextKeywordOffset : d.uniqueKeywords;

      this.applyProgress(
        options.onProgress,
        totalKeywords,
        keywordsDone,
        Date.now() - startedAt,
        d,
        chunksCompleted,
        sumCost
      );

      if (d.uniqueKeywords === 0) {
        break;
      }

      if (d.nextKeywordOffset == null) {
        break;
      }

      keywordOffset = d.nextKeywordOffset;
      if (this.chunkDelayMs > 0) {
        await new Promise((resolve) => setTimeout(resolve, this.chunkDelayMs));
      }
    }

    const elapsedSec = Math.round((Date.now() - startedAt) / 1000);
    return {
      totalKeywords,
      snapshotsWritten: sumSnapshots,
      apiFetches: sumApi,
      cacheHits: sumCache,
      failedKeywords: sumFailedKw,
      estimatedCostUsd: sumCost,
      elapsedSec
    };
  }

  private applyProgress(
    onProgress: ((p: UrlSerpRefreshProgress) => void) | undefined,
    total: number,
    done: number,
    elapsedMs: number,
    chunk: UrlSerpSnapshotsChunkData,
    chunksCompleted: number,
    runningCostUsd: number
  ): void {
    const safeTotal = Math.max(0, total);
    const safeDone = Math.min(safeTotal || done, done);
    const pct =
      safeTotal > 0 ? Math.min(100, Math.round((safeDone / safeTotal) * 100)) : safeDone > 0 ? 50 : 0;

    const elapsedSec = Math.max(elapsedMs / 1000, 0.001);
    const rate = safeDone / elapsedSec;
    const remaining = safeTotal > 0 ? Math.max(0, safeTotal - safeDone) : 0;
    let etaLabel: string | null = null;
    if (remaining > 0 && rate > 0.0005) {
      etaLabel = `About ${this.formatEta(remaining / rate)} remaining`;
    } else if (remaining > 0) {
      etaLabel = 'Estimating time…';
    } else if (safeTotal > 0) {
      etaLabel = 'Finishing up…';
    }

    const costLabel =
      runningCostUsd >= 0.001 ? ` · ~$${runningCostUsd.toFixed(3)} API` : '';
    const kdLabel =
      chunk.keywordDifficultyApiFetches > 0 || chunk.keywordDifficultyCacheHits > 0
        ? ` · KD ${chunk.keywordDifficultyApiFetches} API / ${chunk.keywordDifficultyCacheHits} cached`
        : '';
    const statusLine = `Batch ${chunksCompleted}: ${safeDone}${safeTotal ? ` / ${safeTotal}` : ''} keywords · ${chunk.apiFetches} SERP API · ${chunk.topicSerpCacheHits} SERP cached · ${chunk.snapshotsWritten} snapshots${kdLabel}${costLabel}`;

    onProgress?.({
      percent: pct,
      keywordsDone: safeDone,
      totalKeywords: safeTotal,
      statusLine,
      etaLabel,
      chunksCompleted,
      runningCostUsd
    });
  }

  private formatEta(seconds: number): string {
    if (!Number.isFinite(seconds) || seconds < 0) return '—';
    const capped = Math.min(seconds, 10800);
    if (capped < 8) return 'a few seconds';
    if (capped < 90) return `~${Math.round(capped)}s`;
    const m = Math.floor(capped / 60);
    const s = Math.round(capped - m * 60);
    if (m < 90) return s > 0 && s < 60 ? `~${m}m ${s}s` : `~${m}m`;
    const h = Math.floor(m / 60);
    return `~${h}h ${m % 60}m`;
  }
}
